// @ts-strict-ignore
import { CardsInputBlock } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import { View } from '@shared/components/View';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { LinkedCard } from '@shared/components/blocks/lib/cards';
import {
  useCardItems,
  useGetSummaryCards,
  useOpenLinkedCard,
  useRelatedChannels,
} from '@shared/components/blocks/lib/cards/hooks';
import { defaultFieldPresenter } from '@shared/components/blocks/lib/field';
import { MBCardInputBlockBase } from '@shared/components/blocks/lib/input/base';
import { MBInputCardsBottomSheet } from '@shared/components/blocks/lib/inputCards/sheets';
import { CardFieldItem } from '@shared/components/blocks/types';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isRelatedCardComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext } from '@shared/styles';
import { API } from '@shared/types/api';
import { RelatedCardsList } from './RelatedCardsList';
import { MBCardQuantityPreview } from './quantity/preview';

interface Props {
  block: CardsInputBlock;
}

interface ContentProps {
  block: CardsInputBlock;
  cards: LinkedCard[];
  loading: boolean;
  onEditCards(): void;
  onShowMore(): void;
  onRemoveCard?(card: LinkedCard): void;
  onPressCard(card: LinkedCard): void;
  onChange(cards: LinkedCard[]): void;
}

function MBCardInputBlockCardsContent({
  block,
  cards,
  loading,
  onEditCards,
  onShowMore,
  onRemoveCard,
  onPressCard,
  onChange,
}: ContentProps) {
  const colors = useContext(ColorThemeContext);

  // Hide the icon when there's at least one card
  const blockToRender = useMemo(() => {
    if (isEmpty(cards)) {
      return block;
    } else {
      return { ...block, icon: null };
    }
  }, [block, cards]);

  if (block.mode === 'quantity' && !isEmpty(cards)) {
    return (
      <MBCardQuantityPreview
        editable
        cards={cards}
        label={block.label}
        onAdd={onEditCards}
        onPressCard={onPressCard}
        onChange={onChange}
      />
    );
  }

  const content = (() => {
    if (loading) {
      return <MBLoadingIndicator style={{ alignItems: 'flex-start' }} />;
    }

    if (isEmpty(cards)) {
      return defaultFieldPresenter(
        block.placeholder,
        'text',
        block.textStyle,
        colors.foregroundInactive,
      );
    }

    return (
      <RelatedCardsList
        cards={cards}
        totalCardsNubmer={cards.length}
        block={block}
        onEditCards={onEditCards}
        onRemoveCard={onRemoveCard}
        onPressCard={onPressCard}
        onShowMore={onShowMore}
      />
    );
  })();

  return (
    <MBTouchableOpacity onPress={onEditCards}>
      <MBCardInputBlockBase block={blockToRender} renderLabel={null}>
        {content}
      </MBCardInputBlockBase>
    </MBTouchableOpacity>
  );
}

export function MBCardInputBlockCards({ block }: Props) {
  const { setFieldValue, values: fieldValues } = useContext(CardFormContext);
  const channel = useContext(CardChannelContext);
  const cardItems = useCardItems(useMemo(() => [block.name], [block.name]));
  const { workflowTemplate } = useWorkflowTemplate(channel?.workflowTemplateId);
  const { ids: relatedChannelIds } = useRelatedChannels(block.name);
  const relatedCardComponent = getRelatedCardComponent(workflowTemplate, block);
  const selectedCardIds = cardItems?.map((item: CardFieldItem) => item.id);
  const summaryCards = useGetSummaryCards(selectedCardIds);
  const cards = resolveLinkedCards(summaryCards, cardItems);

  const [showingPicker, setShowingPicker] = useState(false);
  const [viewMode, setViewMode] = useState(false);

  const openLinkedCard = useOpenLinkedCard();

  const onChange = useCallback(
    (updatedCards: LinkedCard[]): void => {
      const defaultQuantity = block.mode === 'quantity' ? 1 : undefined;
      const updatedCardItems = updatedCards.map<CardFieldItem>(item => ({
        id: item.cardId,
        quantity: item.quantity ?? defaultQuantity,
      }));
      setFieldValue(block.name, isEmpty(updatedCardItems) ? null : updatedCardItems);
    },
    [block, setFieldValue],
  );

  const onRemoveCard = useCallback(
    (card: LinkedCard): void => {
      onChange(cards.filter(item => item.cardId !== card.cardId));
    },
    [cards, onChange],
  );

  const showPicker = useCallback(() => setShowingPicker(true), []);
  const hidePicker = useCallback(() => setShowingPicker(false), []);

  const onShowMore = useCallback(() => {
    setViewMode(true);
    showPicker();
  }, [showPicker]);

  const onEditCards = useCallback(() => {
    setViewMode(false);
    showPicker();
  }, [showPicker]);

  const anchorName = `--input-block-cards-${block.name}`;

  return (
    <>
      <View style={{ anchorName }} />
      <MBCardInputBlockCardsContent
        block={block}
        cards={cards}
        loading={false}
        onEditCards={onEditCards}
        onChange={onChange}
        onRemoveCard={onRemoveCard}
        onPressCard={openLinkedCard}
        onShowMore={onShowMore}
      />
      {showingPicker && (
        <MBInputCardsBottomSheet
          block={block}
          fieldValues={fieldValues}
          initialRoute={viewMode ? [{ name: 'view' }] : null}
          max={block.validation?.max}
          values={cards}
          onChange={onChange}
          onRemoveCard={onRemoveCard}
          onClose={hidePicker}
          anchorName={anchorName}
          targetChannelId={channel?.id}
          relatedChannelIds={relatedChannelIds}
          relatedCardComponent={relatedCardComponent}
        />
      )}
    </>
  );
}

function getRelatedCardComponent(
  workflowTemplate: API.WorkflowTemplateFragment | undefined,
  block: CardsInputBlock,
): RelatedCardComponent {
  const component = workflowTemplate?.components.find(workflowComponent => {
    return workflowComponent.id === block.name;
  });
  if (isRelatedCardComponent(component)) {
    return component;
  }
  return null;
}

export function resolveLinkedCards(
  summaryCards: NonNullable<API.ListSummaryCardsQuery['listSummaryCards']['cards']>,
  cardItems: CardFieldItem[],
): LinkedCard[] {
  const cards: LinkedCard[] = [];
  cardItems.forEach((item: CardFieldItem) => {
    const summaryCard = summaryCards?.find(
      (card: NonNullable<API.ListSummaryCardsQuery['listSummaryCards']['cards']>[number]) =>
        item.id === card.id,
    );
    if (!isNil(summaryCard)) {
      cards.push({
        cardId: summaryCard.id,
        name: summaryCard.name,
        quantity: item.quantity,
      });
    }
  });
  return cards;
}
